/**
 * ClientBriefingAttentionCard — Iter-406.
 *
 * "Your Event Briefing Needs Attention" — dark AI Command Center card
 * embedded at the top of the Client Dashboard. Powered by
 *   GET /api/client/briefing/attention
 *
 * Behavior:
 *   • Loads every upcoming client-scoped shift with an incomplete
 *     briefing, sorted by urgency (proximity + criticality).
 *   • For each shift, lists the exact missing sections with an
 *     explanation of why each one matters.
 *   • One-click deep-links to `/client/shifts/{shift_id}` so the
 *     client can add the info.
 *   • Auto-refreshes when the tab becomes visible again + when the
 *     parent calls `onRefresh()`.
 *   • Strictly client-scoped (backend enforces via company_scope_ids).
 *   • Mobile-first responsive: two-column on md+, one-column on phone.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, AlertCircle, ClipboardList, Loader2, ChevronRight,
  RefreshCcw, CheckCircle2, Clock, ExternalLink,
} from 'lucide-react';
import { api } from '../../utils/api';

// Explanation of why each missing section matters — surfaced under
// the section chip so clients understand the cost of leaving it blank.
const WHY_IT_MATTERS = {
  event_overview: 'Talent need context on the campaign to represent your brand well.',
  responsibilities: 'Without clear duties, talent may perform the wrong role onsite.',
  dress_code: 'Missing dress code is the #1 cause of talent showing up wrong.',
  parking_info: 'Parking questions clog your inbox and delay arrival.',
  arrival_instructions: 'Talent lost onsite = late starts and refund requests.',
  onsite_contact: 'When something goes wrong, talent needs a phone number that answers.',
  run_of_show: 'Talent perform better when they know what happens hour-by-hour.',
  product_info: 'Talent selling a product without training = weaker results and returns.',
  attachments: 'PDFs, maps, and slides help talent prep before they arrive.',
};

const URGENCY_META = {
  URGENT:    { label: 'Urgent',      tint: 'rose',    dot: 'bg-rose-500'    },
  THIS_WEEK: { label: 'This week',   tint: 'amber',   dot: 'bg-amber-500'   },
  UPCOMING:  { label: 'Upcoming',    tint: 'sky',     dot: 'bg-sky-500'     },
  SCHEDULED: { label: 'Scheduled',   tint: 'slate',   dot: 'bg-slate-500'   },
};

const TINTS = {
  rose:  'text-rose-300 bg-rose-500/10 border-rose-500/40',
  amber: 'text-amber-300 bg-amber-500/10 border-amber-500/40',
  sky:   'text-sky-300 bg-sky-500/10 border-sky-500/40',
  slate: 'text-slate-300 bg-slate-800/60 border-slate-700/60',
};

function UrgencyChip({ level }) {
  const meta = URGENCY_META[level] || URGENCY_META.SCHEDULED;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${TINTS[meta.tint]}`}
      data-testid={`cbrief-urgency-${level}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
      {meta.label}
    </span>
  );
}

function formatShiftDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso; }
}

function ShiftBriefingRow({ analysis, onOpen }) {
  const critical = analysis.critical_missing || [];
  const missing = analysis.missing_sections || [];
  const daysUntil = analysis.days_until_event;
  const url = analysis.deep_link || `/client/shifts/${analysis.shift_id}`;

  return (
    <div
      className="rounded-xl border border-slate-800/70 bg-slate-900/60 backdrop-blur-sm p-4 flex flex-col gap-3 hover:border-slate-600/80 transition"
      data-testid={`cbrief-row-${analysis.shift_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <UrgencyChip level={analysis.urgency_label} />
            {daysUntil !== null && daysUntil !== undefined && (
              <span className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-800/60 border border-slate-700/60 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" />
                {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `in ${daysUntil} days`}
              </span>
            )}
          </div>
          <div className="font-semibold text-slate-100 mt-1.5 truncate">
            {analysis.event_title || 'Untitled Event'}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {formatShiftDate(analysis.shift_date)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-extrabold text-slate-100 tabular-nums">
            {analysis.completeness_pct}%
          </div>
          <div className="text-[10px] uppercase text-slate-500 tracking-wide">complete</div>
        </div>
      </div>

      {missing.length > 0 && (
        <div className="bg-slate-950/60 border border-slate-800/60 rounded-lg p-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1.5">
            Missing {missing.length} section{missing.length !== 1 ? 's' : ''}
            {critical.length > 0 && (
              <span className="text-rose-300/90 ml-1.5">· {critical.length} critical</span>
            )}
          </div>
          <ul className="space-y-2">
            {missing.slice(0, 5).map((m) => (
              <li key={m.id} className="flex items-start gap-2">
                <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${
                  m.critical ? 'bg-rose-400' : 'bg-slate-500'
                }`} />
                <div className="min-w-0">
                  <div className={`text-xs font-semibold ${
                    m.critical ? 'text-rose-200' : 'text-slate-200'
                  }`}>
                    {m.label}
                  </div>
                  {WHY_IT_MATTERS[m.id] && (
                    <div className="text-[11px] text-slate-400 leading-snug">
                      {WHY_IT_MATTERS[m.id]}
                    </div>
                  )}
                </div>
              </li>
            ))}
            {missing.length > 5 && (
              <li className="text-[11px] text-slate-500 pl-3.5">
                +{missing.length - 5} more…
              </li>
            )}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => onOpen(url)}
        className="mt-auto inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-lg bg-gradient-to-r from-violet-600 to-sky-600 hover:from-violet-500 hover:to-sky-500 text-white text-sm font-semibold transition shadow-sm shadow-violet-900/30"
        data-testid={`cbrief-open-${analysis.shift_id}`}
      >
        Complete this briefing
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function ClientBriefingAttentionCard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ count: 0, counts_by_status: {}, analyses: [] });
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/client/briefing/attention?limit=25');
      setData(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load briefings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh when tab regains focus so info completed elsewhere
  // clears out of this card without a manual reload.
  useEffect(() => {
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  const urgentCount = useMemo(
    () => (data.analyses || []).filter(a => a.urgency_label === 'URGENT' || a.urgency_label === 'THIS_WEEK').length,
    [data.analyses],
  );

  const handleOpen = (url) => {
    // Preserve `?as_client=<uuid>` impersonation context so admins
    // previewing as a client don't lose their scope when clicking into
    // a briefing (same fix pattern as Iter-314 for the invoice route).
    let dest = url;
    try {
      const params = new URLSearchParams(window.location.search);
      const asClient = params.get('as_client');
      if (asClient && !dest.includes('as_client=')) {
        dest = dest + (dest.includes('?') ? '&' : '?') + `as_client=${asClient}`;
      }
    } catch { /* noop */ }
    navigate(dest);
  };

  // Zero-state: hide the card entirely rather than clutter the dashboard.
  if (!loading && !error && (data.count === 0)) {
    return null;
  }

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-slate-800/60 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100 shadow-xl mb-6"
      data-testid="client-briefing-attention-card"
    >
      {/* Subtle radial-dot backdrop */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            'radial-gradient(1px 1px at 20% 20%, rgba(148,163,184,0.14) 1px, transparent 1px), radial-gradient(1px 1px at 80% 60%, rgba(148,163,184,0.12) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative p-4 sm:p-5 md:p-6">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500/30 to-sky-500/30 border border-violet-500/40 flex items-center justify-center shrink-0">
            <ClipboardList className="w-5 h-5 text-violet-200" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-violet-300/90 flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> AI Briefing Assistant
            </div>
            <h2 className="text-lg md:text-xl font-extrabold tracking-tight text-slate-100">
              Your Event Briefing Needs Attention
            </h2>
            <p className="text-xs md:text-sm text-slate-400 mt-0.5">
              {loading
                ? 'Analyzing your upcoming events…'
                : error
                ? error
                : (
                  <>
                    <span className="text-slate-200 font-semibold" data-testid="cbrief-attention-count">
                      {data.count} event{data.count !== 1 ? 's' : ''}
                    </span>{' '}
                    have missing briefing info
                    {urgentCount > 0 && (
                      <>
                        {' '}·{' '}
                        <span className="text-rose-300 font-semibold">
                          {urgentCount} urgent
                        </span>
                      </>
                    )}
                    . Completing these helps your talent show up prepared.
                  </>
                )}
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="shrink-0 inline-flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-slate-700/60 bg-slate-900/40 text-slate-300 hover:bg-slate-800/70 text-xs"
            data-testid="cbrief-refresh"
            title="Refresh"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* Body */}
        {loading && (
          <div className="mt-5 flex items-center justify-center text-slate-400 py-8" data-testid="cbrief-loading">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading…
          </div>
        )}

        {!loading && error && (
          <div className="mt-5 flex items-start gap-2 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3" data-testid="cbrief-error">
            <AlertCircle className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
            <div className="text-xs text-rose-200">{error}</div>
          </div>
        )}

        {!loading && !error && data.count > 0 && (
          <div
            className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3"
            data-testid="cbrief-grid"
          >
            {(data.analyses || []).slice(0, expanded ? data.analyses.length : 6).map((a) => (
              <ShiftBriefingRow
                key={a.shift_id}
                analysis={a}
                onOpen={handleOpen}
              />
            ))}
          </div>
        )}

        {/* Iter-314b — Expand-in-place instead of navigating away to an
            unfiltered `/client/shifts` list (which shows "No shifts yet"
            for billing-only members and any client whose scope excludes
            these briefings). */}
        {!loading && !error && data.count > 6 && !expanded && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-slate-700/60 bg-slate-900/40 text-slate-200 hover:bg-slate-800/70 text-sm"
            data-testid="cbrief-view-all"
          >
            View all {data.count} events needing attention
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
        {!loading && !error && data.count > 6 && expanded && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="mt-4 w-full inline-flex items-center justify-center gap-1.5 h-9 rounded-lg border border-slate-700/60 bg-slate-900/40 text-slate-200 hover:bg-slate-800/70 text-sm"
            data-testid="cbrief-collapse"
          >
            Collapse
          </button>
        )}

        {!loading && !error && data.count === 0 && (
          <div className="mt-5 flex items-center gap-2 text-emerald-300/90" data-testid="cbrief-empty">
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm">All upcoming briefings are complete. Nice.</span>
          </div>
        )}
      </div>
    </div>
  );
}
